import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppConfig } from 'app/config/service.config';
import { MembershipV2Service } from 'app/core/membership/membership-v2.service';
import { OrderService } from 'app/core/order/order.service';
import { PayoutService } from 'app/core/payout/payout.service';
import { ProductService } from 'app/core/product/product.service';
import { TransactionService } from 'app/core/transaction/transaction.service';
import {
    BehaviorSubject,
    Observable,
    Subscription,
    interval,
    switchMap,
    take,
    takeWhile,
    from,
} from 'rxjs';
import { UserService } from '../user/user.service';

export type ExportJobKind =
    | 'order-statement'
    | 'order-delivery'
    | 'pos-orders'
    | 'transaction-statement'
    | 'incentive-ledger'
    | 'payout-summary'
    | 'dynamic-report'
    | 'ep-egp-report'
    | 'sim-record';

export type ExportJobStatusValue =
    | 'PENDING'
    | 'PROCESSING'
    | 'READY'
    | 'FAILED';

export interface PersistentExportJob {
    jobId: string;
    status: ExportJobStatusValue;
    createdAt: string;
    completedAt?: string;
    errorMessage?: string;
    downloadUrl?: string;
    kind: ExportJobKind;
    label: string;
    filenamePrefix: string;
    extension?: string;
    _pollTimedOut?: boolean;
    prompt?: string;
}

@Injectable({ providedIn: 'root' })
export class ExportJobsService {
    private readonly _storageKey = 'eastel.exportJobs';
    private readonly _maxPollAttempts = 60;
    private readonly _jobs = new BehaviorSubject<PersistentExportJob[]>(
        this._loadJobs()
    );
    private readonly _panelOpen = new BehaviorSubject<boolean>(false);
    private readonly _pollSubscriptions = new Map<string, Subscription>();
    private readonly _orderService = inject(OrderService);
    private readonly _transactionService = inject(TransactionService);
    private readonly _productService = inject(ProductService);
    private readonly _membershipV2Service = inject(MembershipV2Service);
    private readonly _payoutService = inject(PayoutService);
    private readonly _userService = inject(UserService);
    private readonly _http = inject(HttpClient);
    private readonly _appConfig = inject(AppConfig);

    readonly jobs$ = this._jobs.asObservable();
    readonly panelOpen$ = this._panelOpen.asObservable();

    constructor() {
        localStorage.removeItem(this._storageKey);

        this._jobs.value
            .filter((job) => this._isActive(job))
            .forEach((job) => this.startPolling(job));

        this._userService.user$.subscribe((user) => {
            if (!user) {
                this.clear();
            }
        });
    }

    request(kind: ExportJobKind, params: any): Observable<PersistentExportJob> {
        return new Observable((subscriber) => {
            const subscription = this._requestByKind(kind, params).subscribe({
                next: (response) => {
                    const job = this._decorateJob(kind, response);
                    this._upsertJob(job);
                    this.startPolling(job);
                    subscriber.next(job);
                    subscriber.complete();
                },
                error: (error) => subscriber.error(error),
            });

            return () => subscription.unsubscribe();
        });
    }

    addDynamicReportJob(jobId: string, prompt: string): void {
        const job: PersistentExportJob = {
            jobId,
            status: 'PENDING',
            createdAt: new Date().toISOString(),
            kind: 'dynamic-report',
            label: `AI: ${prompt}`,
            filenamePrefix: prompt.replace(/\s+/g, '_') || 'report',
            extension: 'xlsx',
            prompt,
        };
        this._upsertJob(job);
        this.startPolling(job);
    }

    startPolling(job: PersistentExportJob): void {
        this._pollSubscriptions.get(job.jobId)?.unsubscribe();

        const subscription = interval(3000)
            .pipe(
                take(this._maxPollAttempts),
                switchMap(() => this._statusByKind(job.kind, job.jobId)),
                takeWhile(
                    (response) =>
                        response.status !== 'READY' &&
                        response.status !== 'FAILED',
                    true
                )
            )
            .subscribe({
                next: (response) => {
                    this._upsertJob({
                        ...this._decorateJob(job.kind, response),
                        label: job.label,
                        filenamePrefix: job.filenamePrefix,
                        extension: job.extension,
                        prompt: job.prompt,
                    });
                },
                complete: () => {
                    this._pollSubscriptions.delete(job.jobId);
                    const current = this._jobs.value.find(
                        (item) => item.jobId === job.jobId
                    );
                    if (current && this._isActive(current)) {
                        this._upsertJob({ ...current, _pollTimedOut: true });
                    }
                },
                error: () => {
                    this._pollSubscriptions.delete(job.jobId);
                },
            });

        this._pollSubscriptions.set(job.jobId, subscription);
    }

    restart(job: PersistentExportJob): void {
        const next = { ...job, _pollTimedOut: false };
        this._upsertJob(next);
        this.startPolling(next);
    }

    download(job: PersistentExportJob): Observable<Blob> {
        switch (job.kind) {
            case 'order-statement':
                return this._orderService.downloadStatementExportFile(
                    job.jobId
                );
            case 'order-delivery':
            case 'pos-orders':
                return this._orderService.downloadExportFile(job.jobId);
            case 'transaction-statement':
                return this._transactionService.downloadAsyncExportFile(
                    job.jobId
                );
            case 'incentive-ledger':
                return this._membershipV2Service.downloadIncentiveLedgerExportFile(
                    job.jobId
                );
            case 'payout-summary':
                return this._payoutService.downloadAsyncExportFile(job.jobId);
            case 'ep-egp-report':
                return this._membershipV2Service.downloadEpEgpReportExportFile(job.jobId);
            case 'sim-record':
                return this._productService.downloadSimRecordExport(job.jobId);
            case 'dynamic-report': {
                const apiServer = this._appConfig.settings.apiServer;
                const baseUrl = (apiServer.useLocalDynamicReport && apiServer.dynamicReportService) || apiServer.eastelService;
                const url = `${baseUrl}/reports/generate-ai/jobs/${job.jobId}/download`;
                const accessToken = localStorage.getItem('accessToken');
                const headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`);
                return this._http.get(url, { responseType: 'blob', headers }).pipe(
                    switchMap((response: Blob) => {
                        return from(this.decodeBase64BlobIfNeeded(response));
                    })
                );
            }
        }
    }

    clear(): void {
        this._pollSubscriptions.forEach((subscription) =>
            subscription.unsubscribe()
        );
        this._pollSubscriptions.clear();
        this._setJobs([]);
        this.closePanel();
    }

    remove(job: PersistentExportJob): void {
        this._pollSubscriptions.get(job.jobId)?.unsubscribe();
        this._pollSubscriptions.delete(job.jobId);

        const jobs = this._jobs.value.filter(
            (item) => !(item.kind === job.kind && item.jobId === job.jobId)
        );
        this._setJobs(jobs);

        if (jobs.length === 0) {
            this.closePanel();
        }
    }

    openPanel(): void {
        this._panelOpen.next(true);
    }

    closePanel(): void {
        this._panelOpen.next(false);
    }

    togglePanel(): void {
        this._panelOpen.next(!this._panelOpen.value);
    }

    private _requestByKind(kind: ExportJobKind, params: any): Observable<any> {
        switch (kind) {
            case 'order-statement':
                return this._orderService.requestAsyncStatementExport(params);
            case 'order-delivery':
            case 'pos-orders':
                return this._orderService.requestAsyncExport(params);
            case 'transaction-statement':
                return this._transactionService.requestAsyncExport(params);
            case 'incentive-ledger':
                return this._membershipV2Service.requestIncentiveLedgerAsyncExport(
                    params
                );
            case 'payout-summary':
                return this._payoutService.requestAsyncExport(
                    params.year,
                    params.month
                );
            case 'ep-egp-report':
                return this._membershipV2Service.requestEpEgpReportExport(params);
            case 'sim-record':
                return this._productService.requestSimRecordExport(params);
            case 'dynamic-report':
                throw new Error('Dynamic reports are requested directly via DynamicReportService');
        }
    }

    private _statusByKind(kind: ExportJobKind, jobId: string): Observable<any> {
        switch (kind) {
            case 'order-statement':
                return this._orderService.getStatementExportJobStatus(jobId);
            case 'order-delivery':
            case 'pos-orders':
                return this._orderService.getExportJobStatus(jobId);
            case 'transaction-statement':
                return this._transactionService.getExportJobStatus(jobId);
            case 'incentive-ledger':
                return this._membershipV2Service.getIncentiveLedgerExportJobStatus(
                    jobId
                );
            case 'payout-summary':
                return this._payoutService.getExportJobStatus(jobId);
            case 'ep-egp-report':
                return this._membershipV2Service.getEpEgpReportExportStatus(jobId);
            case 'sim-record':
                return this._productService.getSimRecordExportStatus(jobId);
            case 'dynamic-report': {
                const apiServer = this._appConfig.settings.apiServer;
                const baseUrl = (apiServer.useLocalDynamicReport && apiServer.dynamicReportService) || apiServer.eastelService;
                const url = `${baseUrl}/reports/generate-ai/jobs/${jobId}`;
                const accessToken = localStorage.getItem('accessToken');
                const headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`);
                return this._http.get<any>(url, { headers });
            }
        }
    }

    private _decorateJob(
        kind: ExportJobKind,
        response: any
    ): PersistentExportJob {
        return {
            jobId: response.jobId,
            status: response.status,
            createdAt: response.createdAt,
            completedAt: response.completedAt,
            errorMessage: response.errorMessage,
            downloadUrl: response.downloadUrl,
            kind,
            label: this._getLabel(kind),
            filenamePrefix: this._getFilenamePrefix(kind),
            extension: this._getExtension(kind),
        };
    }

    private _getLabel(kind: ExportJobKind): string {
        switch (kind) {
            case 'order-statement':
                return 'Order Statement';
            case 'order-delivery':
                return 'Delivery Orders';
            case 'pos-orders':
                return 'POS Orders';
            case 'transaction-statement':
                return 'Transaction Statement';
            case 'incentive-ledger':
                return 'Incentive Ledger';
            case 'payout-summary':
                return 'Payout Summary';
            case 'dynamic-report':
                return 'AI Report';
            case 'ep-egp-report':
                return 'EP/EGP Performance Report';
            case 'sim-record':
                return 'SIM Record Report';
        }
    }

    private _getFilenamePrefix(kind: ExportJobKind): string {
        switch (kind) {
            case 'order-statement':
                return 'orders';
            case 'order-delivery':
                return 'delivery_orders';
            case 'pos-orders':
                return 'online_orders';
            case 'transaction-statement':
                return 'transaction_statement';
            case 'incentive-ledger':
                return 'incentive_ledger';
            case 'payout-summary':
                return 'payout_summary';
            case 'dynamic-report':
                return 'ai_report';
            case 'ep-egp-report':
                return 'ep_egp_performance_report';
            case 'sim-record':
                return 'sim-record-report';
        }
    }

    private _getExtension(kind: ExportJobKind): string {
        return kind === 'sim-record' ? 'csv' : 'xlsx';
    }

    private _upsertJob(job: PersistentExportJob): void {
        const jobs = this._jobs.value.filter(
            (item) => item.jobId !== job.jobId
        );
        this._setJobs([job, ...jobs]);
    }

    private _setJobs(jobs: PersistentExportJob[]): void {
        this._jobs.next(jobs);
        this._saveJobs(jobs);
    }

    private _isActive(job: PersistentExportJob): boolean {
        return job.status === 'PENDING' || job.status === 'PROCESSING';
    }

    private _loadJobs(): PersistentExportJob[] {
        try {
            const raw = sessionStorage.getItem(this._storageKey);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }

    private _saveJobs(jobs: PersistentExportJob[]): void {
        try {
            if (jobs.length === 0) {
                sessionStorage.removeItem(this._storageKey);
                return;
            }

            sessionStorage.setItem(this._storageKey, JSON.stringify(jobs));
        } catch {
            // Keep in-memory state even when browser storage is unavailable.
        }
    }

    private decodeBase64BlobIfNeeded(blob: Blob): Promise<Blob> {
        return blob.text().then((text) => {
            const trimmed = text.trim();
            if (!trimmed) return blob;

            let base64Text = '';
            if (trimmed.startsWith('{')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    let candidate = '';
                    if (parsed && parsed.content && typeof parsed.content === 'object' && parsed.content.text) {
                        candidate = parsed.content.text;
                    } else if (parsed && parsed.content && typeof parsed.content === 'string') {
                        candidate = parsed.content;
                    } else if (parsed && parsed.text && typeof parsed.text === 'string') {
                        candidate = parsed.text;
                    }
                    if (candidate) {
                        candidate = candidate.trim();
                        if (candidate.startsWith('"') && candidate.endsWith('"')) {
                            candidate = candidate.slice(1, -1).trim();
                        }
                        if (candidate.startsWith('UEs')) {
                            base64Text = candidate;
                        }
                    }
                } catch (e) {
                    console.error('Failed to parse response as JSON object:', e);
                }
            }

            if (!base64Text) {
                let candidate = trimmed;
                if (candidate.startsWith('"') && candidate.endsWith('"')) {
                    candidate = candidate.slice(1, -1).trim();
                }
                if (candidate.startsWith('UEs')) {
                    base64Text = candidate;
                }
            }

            if (base64Text) {
                try {
                    const binaryString = window.atob(base64Text);
                    const len = binaryString.length;
                    const bytes = new Uint8Array(len);
                    for (let i = 0; i < len; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    return new Blob([bytes], { type: blob.type });
                } catch (e) {
                    console.error('Failed to decode base64 string:', e);
                }
            }

            return blob;
        });
    }
}
